import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth, can, ROLE } from '../lib/auth';
import { ConsumptionTab, DailySheetTab } from './CuttingQueue';
import { kgValue, qtyText, sheetKg } from '../lib/boardPo';
import { readNumber } from '../lib/formula';
import { NumField, Worked, fmtQty } from '../components/NumField';
import { BoardPoTab, type BoardRef, type ReceivePreset } from './BoardPurchaseOrders';
import { GrnForm, GrnTab } from './Grn';
import { ColumnFilter, type FilterOption } from '../components/ColumnFilter';
import { JobReconcileTab, TXN_LABEL, reverseError } from './BoardJobReconcile';
import { BoardMovementReport } from './BoardMovementReport';
import { MIN_STRIP_IN, cutPlan, isCutSku, parseCutSize } from '../lib/cutLeftover';

/**
 * Board (paper stock) dashboard.
 *
 * A domain-specific view for boards — the operator thinks in SKUs with
 * paper type + dimensions + GSM + brand + location, not in generic item
 * codes.  Backed by tblPaperStock and its own /api/paper-stock endpoints.
 * Raw material and spare parts keep the generic Inventory screen.
 */

interface PaperRow {
  id: number;
  sku: string;
  paperType: string;
  lengthIn: number | null;
  widthIn: number | null;
  gsm: number | null;
  brand: string;
  remarks: string;
  location: string;
  noOfPackets: number;
  sheetPerRim: number | null;
  weightKg: number | null;
  rate: number | null;
  isActive: boolean;
  // Sheets currently available for issue — driven by issue/receive txns.
  sheetsOnHand: number;
  /** Weight of the sheets on hand (null without L / W / GSM); Value = this x rate. */
  stockWeightKg?: number | null;
  totalSheets: number;
  totalValue: number;
}

interface Summary {
  items: number;
  inStock: number;
  outOfStock: number;
  totalSheets: number;
  totalValue: number;
}

type Tab = 'stock' | 'reports' | 'out' | 'po' | 'grn' | 'reconcile' | 'count' | 'requests';

const TABS: { key: Tab; label: string; badgeKey?: keyof Summary }[] = [
  { key: 'stock',     label: 'Stock' },
  { key: 'reports',   label: 'Reports' },
  { key: 'out',       label: 'Out of Stock', badgeKey: 'outOfStock' },
  { key: 'po',        label: 'Purchase Order' },
  // Every delivery booked in with its received date.
  { key: 'grn',       label: 'GRN' },
  { key: 'reconcile', label: 'Reconcile' },
  { key: 'count',     label: 'Stock Count' },
  { key: 'requests',  label: 'Requests' },
];

/** Stock-table columns with an Excel-style filter in the header. */
const FILTER_COLS = ['paperType', 'lengthIn', 'widthIn', 'gsm', 'brand', 'remarks', 'location', 'rate'] as const;
type FilterKey = typeof FILTER_COLS[number];
const FILTER_LABEL: Record<FilterKey, string> = {
  paperType: 'Paper', lengthIn: 'Length', widthIn: 'Width', gsm: 'GSM',
  brand: 'Brand', remarks: 'Remarks', location: 'Location', rate: 'Rate',
};

/**
 * A cell as the column filters see it.  Text matches ignoring case (ITC and
 * itc are one entry, as in Excel) and shows as first written; '' is blank.
 */
function filterCell(r: PaperRow, k: FilterKey): FilterOption {
  const v = r[k];
  // A board with no price shows as blank, so (Blanks) finds the unpriced ones.
  if (k === 'rate' && !Number(v)) return { key: '', label: '' };
  if (v == null || String(v).trim() === '') return { key: '', label: '' };
  if (typeof v === 'number') return { key: String(v), label: String(v) };
  const s = String(v).trim();
  return { key: s.toUpperCase(), label: s };
}

/** Indian number format: 12,34,567 rather than 1,234,567. */
function fmtIN(n: number, digits = 0): string {
  return n.toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits });
}

export function PaperStockDashboardPage() {
  const { user } = useAuth();
  // Super Admin and Purchase work the stock; Accounts and GM look at it.  The
  // route checks this too.
  // VIEW_BOARDS alone (the Planner): the Stock list to look at and search.
  const boardsOnly = !can(user, 'VIEW_INVENTORY') && can(user, 'VIEW_BOARDS');
  if (!can(user, 'VIEW_INVENTORY') && !boardsOnly) {
    return <div className="p-6 text-slate-500">You do not have access to the inventory.</div>;
  }
  // Without it the page is to look at and filter: no Add, Receive, Issue or
  // edit — the API refuses those anyway.
  const canManage = can(user, 'MANAGE_INVENTORY');

  const qc = useQueryClient();
  // The tab lives in the URL (/inventory/boards/po) so it can be linked to
  // and survives a refresh.
  const params = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  // Stock Count is Super Admin's alone for now (the API refuses anyone else).
  const tabs = TABS.filter(t => (boardsOnly ? t.key === 'stock' || t.key === 'reports' : t.key !== 'count' || user?.rollId === ROLE.SuperAdmin));
  const tab: Tab = tabs.some(t => t.key === params.tab) ? params.tab as Tab : 'stock';
  const setTab = (t: Tab) => navigate(t === 'stock' ? '/inventory/boards' : `/inventory/boards/${t}`);
  const [q, setQ] = useState('');
  const [paperType, setPaperType] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sort, setSort] = useState('sku');
  const [editing, setEditing] = useState<PaperRow | null>(null);
  const [issuing, setIssuing] = useState<PaperRow | null>(null);
  const [receiving, setReceiving] = useState<{ item: PaperRow; preset?: ReceivePreset } | null>(null);
  const [history, setHistory] = useState<PaperRow | null>(null);
  const [colFilters, setColFilters] = useState<Partial<Record<FilterKey, Set<string>>>>({});

  const listArgs = { q, paperType, inStockOnly, sort, tab };

  const list = useQuery({
    queryKey: ['paper-stock', listArgs],
    queryFn: () => {
      const sp = new URLSearchParams();
      if (q) sp.set('q', q);
      if (paperType) sp.set('paperType', paperType);
      if (inStockOnly || tab === 'out') sp.set('inStockOnly', tab === 'out' ? '0' : '1');
      sp.set('sort', sort);
      return api<{ rows: PaperRow[] }>(`/api/paper-stock?${sp.toString()}`);
    },
  });

  const summary = useQuery({
    queryKey: ['paper-stock-summary'],
    queryFn: () => api<Summary>('/api/paper-stock/summary'),
  });

  const types = useQuery({
    queryKey: ['paper-stock-types'],
    queryFn: () => api<{ rows: string[] }>('/api/paper-stock/paper-types'),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['paper-stock'] });
    qc.invalidateQueries({ queryKey: ['paper-stock-summary'] });
    qc.invalidateQueries({ queryKey: ['paper-stock-types'] });
    // A receipt quoting a PO number moves that PO's received count.
    qc.invalidateQueries({ queryKey: ['board-pos'] });
    qc.invalidateQueries({ queryKey: ['board-po'] });
    qc.invalidateQueries({ queryKey: ['board-po-open-for'] });
  };

  const rows = list.data?.rows ?? [];
  // Client-side filter for the Out of Stock tab reads the live sheet counter,
  // not NoOfPackets — a board can have a physical packet count but zero
  // sheets left after issues.
  const shownRows = tab === 'out' ? rows.filter(r => r.sheetsOnHand <= 0) : rows;

  // Excel-style header filters, on top of the search / type / stock controls.
  // A column's list offers the values in rows that pass the OTHER columns'
  // filters, as Excel does.
  const passes = (r: PaperRow, except?: FilterKey) =>
    FILTER_COLS.every(k => k === except || !colFilters[k] || colFilters[k]!.has(filterCell(r, k).key));
  const tableRows = shownRows.filter(r => passes(r));
  const optionsFor = (k: FilterKey): FilterOption[] => {
    const seen = new Map<string, string>();
    for (const r of shownRows) {
      if (!passes(r, k)) continue;
      const c = filterCell(r, k);
      if (!seen.has(c.key)) seen.set(c.key, c.label);
    }
    return [...seen].map(([key, label]) => ({ key, label }));
  };
  const setColFilter = (k: FilterKey, next: Set<string> | null) =>
    setColFilters(f => {
      const n = { ...f };
      if (next) n[k] = next; else delete n[k];
      return n;
    });
  const activeFilters = FILTER_COLS.filter(k => colFilters[k]);
  const filterBtn = (k: FilterKey, numeric = false) => (
    <ColumnFilter
      label={FILTER_LABEL[k]}
      options={optionsFor(k)}
      selected={colFilters[k] ?? null}
      onChange={next => setColFilter(k, next)}
      numeric={numeric}
    />
  );

  const s = summary.data;
  // The tiles add up the boards the table lists once anything narrows it — a
  // search, the paper type, "In stock only", a column filter, the Out of
  // Stock tab.  The other tabs, and the Out of Stock badge, show all stock.
  const narrowed = (tab === 'stock' || tab === 'out') && !!list.data
    && (!!q.trim() || !!paperType || inStockOnly || activeFilters.length > 0 || tab === 'out');
  const t: Summary | undefined = narrowed ? totalsOf(tableRows) : s;

  return (
    <div className="p-6">
      {/* ── Header + stats + actions ─────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-xl font-semibold text-brand">Paper Stock Dashboard</h1>
          <p className="text-xs text-slate-500 uppercase tracking-wide">Vasudev Print Pack · Board Inventory</p>
        </div>
        <div className="flex items-center gap-6 flex-wrap">
          <Stat value={t ? fmtIN(t.items) : '—'} label={narrowed ? 'Items · filtered' : 'Items'} tone="slate" />
          <Stat value={t ? fmtIN(t.inStock) : '—'} label="In Stock" tone="emerald" />
          <Stat value={t ? fmtIN(t.outOfStock) : '—'} label="Out of Stock" tone="red" />
          <Stat value={t ? fmtIN(t.totalSheets) : '—'} label="Total Sheets" tone="slate" />
          <Stat value={t ? fmtIN(t.totalValue) : '—'} label="Total Value" tone="amber" />
          <div className="flex items-center gap-2">
            {canManage
              ? <button className="btn-primary" onClick={() => setEditing(emptyRow())}>+ Add Item</button>
              : <span className="rounded bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600" title="You can look and filter; stock is kept by Purchase">View only</span>}
            <button className="btn-ghost" onClick={refresh} title="Refresh">↻ Refresh</button>
          </div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="flex gap-4 mb-4 border-b border-slate-200">
        {tabs.map(t => (
          <button
            key={t.key}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 ${
              tab === t.key ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.badgeKey && s && s[t.badgeKey] > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 text-[10px] font-semibold px-1.5 py-0.5">
                {fmtIN(s[t.badgeKey] as number)}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Controls (Stock + Out of Stock tabs) ─────────────────── */}
      {(tab === 'stock' || tab === 'out') && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <input
              className="input flex-1 min-w-[240px]"
              placeholder="Search — try: Grey Back 26.5 38.5 340 Genius E1"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
            <select
              className="input max-w-[200px]"
              value={paperType}
              onChange={e => setPaperType(e.target.value)}
            >
              <option value="">All paper types</option>
              {(types.data?.rows ?? []).map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap">
              <input type="checkbox" checked={inStockOnly} onChange={e => setInStockOnly(e.target.checked)} />
              In stock only
            </label>
            <select className="input max-w-[200px]" value={sort} onChange={e => setSort(e.target.value)}>
              <option value="sku">Sort: SKU (A→Z)</option>
              <option value="sku-desc">Sort: SKU (Z→A)</option>
              <option value="sheets-desc">Sort: Sheets on Hand (high→low)</option>
              <option value="sheets">Sort: Sheets on Hand (low→high)</option>
              <option value="length">Sort: Length (low→high)</option>
              <option value="length-desc">Sort: Length (high→low)</option>
              <option value="gsm">Sort: GSM (low→high)</option>
              <option value="gsm-desc">Sort: GSM (high→low)</option>
              <option value="packets">Sort: Packets (low→high)</option>
              <option value="packets-desc">Sort: Packets (high→low)</option>
              <option value="weight-desc">Sort: Weight (heavy→light)</option>
            </select>
          </div>
          <div className="text-xs text-slate-500 mb-3">
            Tip: type any part of a SKU, paper type, brand, dimension, GSM or location.
          </div>

          <div className="bg-white rounded shadow overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <Th>SKU</Th>
                  <Th>Paper{filterBtn('paperType')}</Th>
                  <Th numeric>Length{filterBtn('lengthIn', true)}</Th>
                  <Th numeric>Width{filterBtn('widthIn', true)}</Th>
                  <Th numeric>GSM{filterBtn('gsm', true)}</Th>
                  <Th>Brand{filterBtn('brand')}</Th>
                  <Th>Remarks{filterBtn('remarks')}</Th>
                  <Th>Location{filterBtn('location')}</Th>
                  <Th numeric>Sheets on Hand</Th>
                  <Th>Action</Th>
                  <Th numeric>Packets</Th>
                  <Th numeric>Sheet/Rim</Th>
                  <Th numeric>Weight (kg)</Th>
                  <Th numeric>Rate (₹/kg){filterBtn('rate', true)}</Th>
                  <Th numeric>Value</Th>
                </tr>
              </thead>
              <tbody>
                {list.isLoading ? (
                  <tr><td colSpan={15} className="p-6 text-center text-slate-400">Loading…</td></tr>
                ) : tableRows.length === 0 ? (
                  <tr><td colSpan={15} className="p-6 text-center text-slate-400">No items match.</td></tr>
                ) : tableRows.map(r => (
                  <tr key={r.id} className={`border-t border-slate-100 hover:bg-yellow-200 font-semibold text-slate-800 ${r.sheetsOnHand <= 0 ? 'bg-red-50/40' : ''}`}>
                    <td className="px-3 py-1.5 font-mono font-bold text-brand">{r.sku}</td>
                    <td className="px-3 py-1.5">{r.paperType || '—'}</td>
                    <td className="px-3 py-1.5 text-right">{r.lengthIn ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">{r.widthIn ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right">{r.gsm ?? '—'}</td>
                    <td className="px-3 py-1.5">{r.brand || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-600 max-w-[160px] truncate" title={r.remarks}>{r.remarks || '—'}</td>
                    <td className="px-3 py-1.5">{r.location || '—'}</td>
                    <td className={`px-3 py-1.5 text-right font-bold ${r.sheetsOnHand <= 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                      {fmtIN(r.sheetsOnHand)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap font-bold">
                      {canManage && (
                        <>
                          {!isCutSku(r.sku) && (
                            <button
                              className="text-emerald-700 hover:underline mr-2"
                              title="Record incoming stock"
                              onClick={() => setReceiving({ item: r })}
                            >+ Receive</button>
                          )}
                          <button
                            className="text-brand hover:underline mr-2 disabled:text-slate-300 disabled:no-underline disabled:cursor-not-allowed"
                            title="Issue sheets to a Job Card"
                            onClick={() => setIssuing(r)}
                            disabled={r.sheetsOnHand <= 0}
                          >Issue</button>
                        </>
                      )}
                      <button
                        className="text-slate-700 hover:text-brand hover:underline mr-2"
                        title="Transaction history"
                        onClick={() => setHistory(r)}
                      >History</button>
                      {canManage && <button className="text-slate-600 hover:text-brand hover:underline" onClick={() => setEditing(r)}>edit</button>}
                    </td>
                    <td className="px-3 py-1.5 text-right">{r.noOfPackets}</td>
                    <td className="px-3 py-1.5 text-right">{r.sheetPerRim ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right" title="Weight of the sheets on hand">{r.stockWeightKg != null ? fmtIN(r.stockWeightKg, 2) : (r.weightKg ?? '—')}</td>
                    <td className="px-3 py-1.5 text-right">{r.rate ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right font-bold text-amber-700">{fmtIN(r.totalValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-400 mt-2">
            {tableRows.length} item(s) shown
            {activeFilters.length > 0 && (
              <>
                {' '}· filtered by {activeFilters.map(k => FILTER_LABEL[k]).join(', ')}
                {' '}·{' '}
                <button type="button" className="text-brand underline hover:text-brand-700" onClick={() => setColFilters({})}>
                  Clear all filters
                </button>
              </>
            )}
          </div>
        </>
      )}

      {/* ── Placeholder tabs (built next) ────────────────────────── */}
      {tab === 'reports' && <BoardReportsTab />}
      {tab === 'po' && (
        <BoardPoTab onReceive={(board, preset) => setReceiving({ item: boardToRow(board), preset })} />
      )}
      {tab === 'grn' && <GrnTab kind="BOARD" />}
      {tab === 'reconcile' && <JobReconcileTab onChanged={refresh} />}
      {tab === 'count' && <StockCountTab onPosted={refresh} />}
      {tab === 'requests' && <RequestsTab />}

      {editing && (
        <ItemEditor
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
      {issuing && (
        <IssueModal
          item={issuing}
          onClose={() => setIssuing(null)}
          onSaved={() => { setIssuing(null); refresh(); }}
        />
      )}
      {/* A delivery comes in through a GRN: this board on its first line. */}
      {receiving && (
        <GrnForm
          kind="BOARD"
          start={{
            paperStockId: receiving.item.id, poNumber: receiving.preset?.poNumber, qty: receiving.preset?.qty,
            rate: receiving.preset?.rate, ratePerKg: receiving.preset?.ratePerKg,
          }}
          onClose={() => setReceiving(null)}
          onSaved={refresh}
        />
      )}
      {history && (
        <HistoryModal item={history} onClose={() => setHistory(null)} />
      )}
    </div>
  );
}

/* ---------------- helpers / sub-components ------------------------ */

/** The tiles for a set of boards — the same sums /summary makes, value in whole paise. */
function totalsOf(rows: PaperRow[]): Summary {
  return {
    items: rows.length,
    inStock: rows.filter(r => r.sheetsOnHand > 0).length,
    outOfStock: rows.filter(r => r.sheetsOnHand <= 0).length,
    totalSheets: rows.reduce((n, r) => n + r.sheetsOnHand, 0),
    totalValue: rows.reduce((p, r) => p + Math.round(r.totalValue * 100), 0) / 100,
  };
}

const emptyRow = (): PaperRow => ({
  id: 0, sku: '', paperType: '', lengthIn: null, widthIn: null, gsm: null,
  brand: '', remarks: '', location: '', noOfPackets: 0, sheetPerRim: null,
  weightKg: null, rate: null, isActive: true, sheetsOnHand: 0, totalSheets: 0, totalValue: 0,
});

/** Enough of a stock row for the Receive form, from a PO line's board. */
const boardToRow = (b: BoardRef): PaperRow => ({
  ...emptyRow(),
  id: b.id, sku: b.sku, paperType: b.paperType, lengthIn: b.lengthIn, widthIn: b.widthIn,
  gsm: b.gsm, brand: b.brand, rate: b.rate, sheetsOnHand: b.sheetsOnHand, totalSheets: b.sheetsOnHand,
});

function Th({ children, numeric }: { children: React.ReactNode; numeric?: boolean }) {
  return (
    <th className={`px-3 py-2 font-medium text-slate-600 whitespace-nowrap ${numeric ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}

function Stat({ value, label, tone }: { value: string; label: string; tone: 'slate' | 'emerald' | 'red' | 'amber' }) {
  const colour = {
    slate: 'text-slate-800',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
  }[tone];
  return (
    <div className="flex flex-col items-end leading-none">
      <div className={`text-lg font-bold ${colour}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

/** kg and ₹ to 2 decimals — what the store reads off the invoice. */
const r2 = (n: number) => Math.round(n * 100) / 100;
/** The first number field that can't be used, as a message; null when all are fine. */
function badNumber(fields: [string, number | null][]): string | null {
  const bad = fields.find(([, v]) => v != null && (Number.isNaN(v) || v < 0));
  if (!bad) return null;
  return `${bad[0]}: ${Number.isNaN(bad[1]) ? "can't read that — use a number or =formula" : "can't be negative"}.`;
}

const NO_STOCK_FOR_INVOICE =
  'Fill in No. of Packets and Sheet / Rim — the PO and invoice go with the opening stock.';

/** A refused add or edit: the board already has a SKU (same paper type, size, GSM and brand). */
function duplicateText(m: string, isNew: boolean): string | null {
  if (!m.startsWith('duplicate_board:')) return null;
  const sku = m.slice('duplicate_board:'.length);
  if (isNew && isCutSku(sku)) return `This cutting stock is already ${sku} (same paper type, size, GSM and brand).`;
  return isNew
    ? `This board is already ${sku} (same paper type, size, GSM and brand). Use + Receive on ${sku} instead of adding it again.`
    : `${sku} is already this board (same paper type, size, GSM and brand). Merge the two instead of making a copy.`;
}

function ItemEditor({ item, onClose, onSaved }: {
  item: PaperRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = !item.id;
  // Editing a board without EDIT_MASTERS (Purchase): paper type, brand and
  // location only — the API keeps the rest as it is regardless.
  const { user } = useAuth();
  const locked = !isNew && !can(user, 'EDIT_MASTERS');
  const [form, setForm] = useState<PaperRow>(item);
  const [err, setErr] = useState<string | null>(null);

  // Number fields are held as typed, so they can take a formula ("=5.5*100").
  const txt = (n: number | null | undefined) => (n == null ? '' : String(n));
  const [lengthText, setLengthText] = useState(txt(item.lengthIn));
  const [widthText, setWidthText] = useState(txt(item.widthIn));
  const [gsmText, setGsmText] = useState(txt(item.gsm));
  const [packetsText, setPacketsText] = useState(isNew ? '' : txt(item.noOfPackets));
  const [sprText, setSprText] = useState(txt(item.sheetPerRim));
  const [weightText, setWeightText] = useState('');
  const [rateText, setRateText] = useState(txt(item.rate));
  // A new board's opening stock is booked in as a receipt carrying these.
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [poNumber, setPoNumber] = useState('');

  const L = readNumber(lengthText);
  const W = readNumber(widthText);
  const g = readNumber(gsmText);
  const packets = readNumber(packetsText);
  const spr = readNumber(sprText);
  const weightTyped = readNumber(weightText);
  const rate = readNumber(rateText);
  const pos = (v: number | null): v is number => v != null && Number.isFinite(v) && v > 0;

  // Weight = L (in) × W (in) × GSM (g/m²) × sheets × 0.00064516 / 1000, where
  // 0.00064516 converts a square-inch area to m² (1 in² = 6.4516 cm² = 6.4516e-4 m²).
  // A 25×36 in, 300 GSM, 1000-sheet stack works out to ~174 kg — matches the
  // shop's standard weight sheet.  Blank until Length, Width, GSM, packets and
  // sheet/rim are all in; a typed weight overrides it.
  const weightAuto = pos(L) && pos(W) && pos(g) && pos(packets) && pos(spr)
    ? r2((L * W * g * packets * spr * 0.00064516) / 1000) : null;
  const weight = weightTyped ?? weightAuto;
  // Rate is per kg, so the stock's value is its weight x rate.
  const value = pos(weight) && pos(rate) ? kgValue(weight, rate) : null;

  // Editing a board's packets or sheet/rim sets its stock to packets x
  // sheet/rim (the API books the difference as an adjustment).  The form
  // opens on the live packets (on hand / sheet per rim), so a save that
  // leaves both alone never moves the stock.
  const sprWhole = spr != null ? Math.round(spr) : null;
  const restock = !isNew && !locked && packets != null && packets >= 0 && pos(sprWhole)
    && (packets !== item.noOfPackets || sprWhole !== item.sheetPerRim);
  const restockSheets = restock ? Math.round(packets! * sprWhole!) : null;

  const save = useMutation({
    mutationFn: (nums: { lengthIn: number | null; widthIn: number | null; gsm: number | null; noOfPackets: number;
      sheetPerRim: number | null; weightKg: number | null; rate: number | null; stockFromPackets?: boolean;
      poNumber?: string | null; invoiceNumber?: string | null; invoiceDate?: string | null }) => api(
      isNew ? '/api/paper-stock' : `/api/paper-stock/${item.id}`,
      {
        method: isNew ? 'POST' : 'PUT',
        body: JSON.stringify({
          // Omit sku entirely on create so the API auto-generates the next SKU#
          // code; still send the operator's value on edit so the code stays
          // as-is unless they explicitly change it.
          ...(isNew ? {} : { sku: form.sku }),
          paperType: form.paperType || null,
          brand: form.brand || null,
          remarks: form.remarks || null,
          location: form.location || null,
          isActive: form.isActive,
          ...nums,
        }),
      },
    ),
    onSuccess: onSaved,
    onError: (e) => {
      const m = (e as Error).message;
      setErr(duplicateText(m, isNew) ?? (m === 'invoice_needs_stock' ? NO_STOCK_FOR_INVOICE
        : m === 'sheet_per_rim_needed' ? 'Fill in Sheet / Rim — sheets on hand are packets × sheet/rim.'
        : m));
    },
  });
  // Super Admin merges a copy of this board into the SKU to keep.
  const [merging, setMerging] = useState(false);
  const canMerge = !isNew && user?.rollId === ROLE.SuperAdmin;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    // On edit only — new boards get their SKU auto-assigned by the API.
    if (!isNew && !form.sku.trim()) { setErr('SKU is required'); return; }
    // The locked fields are not this user's to fix, so they are not checked.
    const problem = locked ? null : badNumber([
      ['Length', L], ['Width', W], ['GSM', g], ['No. of Packets', packets],
      ['Sheet / Rim', spr], ['Weight', weightTyped], ['Rate', rate],
    ]);
    if (problem) { setErr(problem); return; }
    const whole = (v: number | null) => v == null || Math.abs(v - Math.round(v)) < 1e-6;
    if (!locked && !whole(g)) { setErr('GSM must be a whole number.'); return; }
    if (!locked && !whole(spr)) { setErr('Sheet / Rim must be a whole number.'); return; }
    const sheets = (packets ?? 0) * (spr != null ? Math.round(spr) : 0);
    // A new board's packets are its opening receipt, so they must come to
    // whole sheets.  An edit rounds to the nearest sheet instead: a loaded
    // board's 5.87 x 144 (845.28) is 845 sheets, and shows so before saving.
    if (isNew && !whole(sheets)) { setErr(`No. of Packets × Sheet / Rim must come to whole sheets (it comes to ${fmtQty(sheets)}).`); return; }
    const paperwork = isNew && !!(invoiceDate || invoiceNumber.trim() || poNumber.trim());
    if (paperwork && Math.round(sheets) <= 0) { setErr(NO_STOCK_FOR_INVOICE); return; }
    save.mutate({
      lengthIn: L,
      widthIn: W,
      gsm: g != null ? Math.round(g) : null,
      noOfPackets: packets ?? 0,
      sheetPerRim: spr != null ? Math.round(spr) : null,
      weightKg: weight != null ? r2(weight) : null,
      rate,
      ...(restock ? { stockFromPackets: true } : {}),
      ...(isNew ? {
        poNumber: poNumber.trim() || null,
        invoiceNumber: invoiceNumber.trim() || null,
        invoiceDate: invoiceDate || null,
      } : {}),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h2 className="text-lg font-semibold mb-4">{isNew ? 'Add board' : `Edit ${item.sku}`}</h2>
        <div className="grid grid-cols-3 gap-3">
          {isNew
            ? (
              <div>
                <label className="label">SKU (Auto)</label>
                <input className="input bg-slate-50 text-slate-400" value="Auto-generated" disabled />
              </div>
            )
            : <F label="SKU *" value={form.sku} onChange={v => setForm({ ...form, sku: v })} disabled={locked} />}
          <F label="Paper type" value={form.paperType} onChange={v => setForm({ ...form, paperType: v })} placeholder="WHITE BACK" />
          <F label="Brand" value={form.brand} onChange={v => setForm({ ...form, brand: v })} placeholder="ITC / KHANNA / …" />

          <NumField label="Length (in)" text={lengthText} onText={setLengthText} value={L} placeholder="e.g. 20" disabled={locked} />
          <NumField label="Width (in)" text={widthText} onText={setWidthText} value={W} placeholder="e.g. 38" disabled={locked} />
          <NumField label="GSM" text={gsmText} onText={setGsmText} value={g} placeholder="e.g. 350" disabled={locked} />

          <NumField label="No. of Packets" text={packetsText} onText={setPacketsText} value={packets} placeholder="e.g. 5.5 or =formula" disabled={locked} />
          <NumField label="Sheet / Rim" text={sprText} onText={setSprText} value={spr} placeholder="e.g. 100" disabled={locked} />
          <div>
            <label className="label">Weight (kg)</label>
            <input className="input disabled:bg-slate-50 disabled:text-slate-500" inputMode="decimal" value={weightText} onChange={e => setWeightText(e.target.value)}
              placeholder={weightAuto != null ? `Auto: ${fmtQty(weightAuto)}` : 'Auto-calc or =formula'} disabled={locked}
              title="Worked out from Length, Width, GSM, No. of Packets and Sheet / Rim; type a weight to override." />
            <Worked text={weightText} value={weightTyped} unit=" kg" />
            {weightTyped == null && weightAuto != null && (
              <div className="text-[11px] text-slate-500 mt-0.5">Auto: L × W × GSM × sheets</div>
            )}
          </div>

          <NumField label="Rate (per kg)" text={rateText} onText={setRateText} value={rate} placeholder="e.g. 46.50 or =formula" disabled={locked} />
          <div>
            <label className="label">Value (Auto: Weight x Rate)</label>
            <input className="input bg-slate-50" readOnly tabIndex={-1} value={value != null ? `₹ ${fmtIN(value, 2)}` : ''} />
          </div>
          <F label="Location" value={form.location} onChange={v => setForm({ ...form, location: v })} placeholder="H BLOCK" />
          {isNew ? (
            <>
              <div>
                <label className="label">Invoice Date</label>
                <input type="date" className="input" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
              </div>
              <F label="Invoice Number" value={invoiceNumber} onChange={setInvoiceNumber} placeholder="e.g. INV-2024-001" />
              <F label="PO Number" value={poNumber} onChange={setPoNumber} placeholder="e.g. PO-12345" />
            </>
          ) : restockSheets != null ? (
            <div className={`col-span-3 -mt-1 rounded border px-3 py-2 text-xs ${restockSheets >= item.sheetsOnHand
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
              Sheets on hand: {fmtIN(item.sheetsOnHand)} → <b className="text-sm">{fmtIN(restockSheets)}</b>
              {' '}({fmtQty(packets!)} packets × {sprWhole}{Math.abs(packets! * sprWhole! - restockSheets) > 1e-6 ? ', rounded' : ''}).
              {' '}Saved as an adjustment in History.
            </div>
          ) : (
            <div className="col-span-3 -mt-1 text-[11px] text-slate-500">
              On hand now: <b>{fmtIN(item.sheetsOnHand)} sheets</b>
              {item.stockWeightKg != null && <> · {fmtIN(item.stockWeightKg, 2)} kg · ₹{fmtIN(item.totalValue, 2)}</>}
              {' '}— Receive and Issue change it{locked ? '' : '; so does changing No. of Packets or Sheet / Rim here (packets × sheet/rim)'}.
              {' '}PO number and invoice are on each receipt, in History.
            </div>
          )}

          <div className="col-span-3">
            <label className="label">Remarks</label>
            <input
              className="input disabled:bg-slate-50 disabled:text-slate-500"
              value={form.remarks}
              onChange={e => setForm({ ...form, remarks: e.target.value })}
              placeholder="Free-form notes"
            />
          </div>
          <label className="col-span-3 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} disabled={locked} />
            Active
          </label>
          {locked && (
            <div className="col-span-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              You can change Paper type, Brand, Location and Remarks. The rest is set by Super Admin.
            </div>
          )}
        </div>

        {err && <div className="mt-3 text-sm text-red-700">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          {canMerge && (
            <button type="button" className="mr-auto text-sm text-slate-600 hover:text-brand underline" onClick={() => setMerging(true)}>
              Merge into another SKU…
            </button>
          )}
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
      {merging && <MergeModal item={item} onClose={() => setMerging(false)} onMerged={onSaved} />}
    </div>
  );
}

interface MergeCandidate extends PaperRow { sameBoard: boolean }

function mergeError(e: unknown): string {
  const m = (e as Error).message;
  if (m === 'merge_size_mismatch') return 'Only SKUs of the same size and GSM can be merged.';
  if (m === 'not_found') return 'One of the SKUs is gone — refresh and try again.';
  if (m === 'super_admin_only') return 'Only Super Admin can merge SKUs.';
  return `Merge failed: ${m}`;
}

/**
 * Super Admin: two SKUs for one board.  This one is retired; its stock, its
 * History and its PO lines move into the SKU picked, which keeps its own
 * code and fills in anything it lacks.  Offered: SKUs of the same size and
 * GSM, the exact copies first.
 */
function MergeModal({ item, onClose, onMerged }: { item: PaperRow; onClose: () => void; onMerged: () => void }) {
  const cands = useQuery({
    queryKey: ['board-merge-candidates', item.id],
    queryFn: () => api<{ rows: MergeCandidate[] }>(`/api/paper-stock/${item.id}/merge-candidates`),
  });
  const rows = cands.data?.rows ?? [];
  const [pick, setPick] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // The one exact copy is the likely pick.
  useEffect(() => {
    if (pick == null && rows.filter(r => r.sameBoard).length === 1) setPick(rows[0]!.id);
  }, [rows, pick]);
  const target = rows.find(r => r.id === pick) ?? null;
  const merge = useMutation({
    mutationFn: () => api(`/api/paper-stock/${item.id}/merge`, { method: 'POST', body: JSON.stringify({ intoId: pick }) }),
    onSuccess: onMerged,
    onError: (e) => setErr(mergeError(e)),
  });

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-1 text-brand">Merge {item.sku} into another SKU</h2>
        <p className="text-xs text-slate-600 mb-4">
          For two SKUs of the same board. <b>{item.sku}</b> is retired — its <b>{fmtIN(item.sheetsOnHand)} sheets</b>, its History
          and its PO lines move into the SKU you pick. This can't be undone.
        </p>
        {cands.isLoading ? (
          <div className="text-sm text-slate-400">Loading…</div>
        ) : cands.isError ? (
          <div className="text-sm text-red-700">{mergeError(cands.error)}</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-slate-500">
            No other SKU has the same size and GSM ({item.lengthIn ?? '—'} × {item.widthIn ?? '—'}, {item.gsm ?? '—'} GSM).
          </div>
        ) : (
          <div className="border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-3 py-2" />
                  <th className="px-3 py-2 text-left">SKU</th>
                  <th className="px-3 py-2 text-left">Paper</th>
                  <th className="px-3 py-2 text-left">Brand</th>
                  <th className="px-3 py-2 text-left">Location</th>
                  <th className="px-3 py-2 text-right">Sheets on Hand</th>
                  <th className="px-3 py-2 text-left" />
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className={`border-t border-slate-100 cursor-pointer ${pick === r.id ? 'bg-yellow-100' : 'hover:bg-slate-50'}`} onClick={() => setPick(r.id)}>
                    <td className="px-3 py-1.5"><input type="radio" checked={pick === r.id} onChange={() => setPick(r.id)} /></td>
                    <td className="px-3 py-1.5 font-mono font-bold text-brand">{r.sku}</td>
                    <td className="px-3 py-1.5">{r.paperType || '—'}</td>
                    <td className="px-3 py-1.5">{r.brand || '—'}</td>
                    <td className="px-3 py-1.5">{r.location || '—'}</td>
                    <td className="px-3 py-1.5 text-right font-semibold">{fmtIN(r.sheetsOnHand)}</td>
                    <td className="px-3 py-1.5">
                      {r.sameBoard
                        ? <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">Same board</span>
                        : <span className="text-[10px] text-amber-700">Other paper type or brand</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {target && (
          <div className="mt-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700">
            After the merge <b>{target.sku}</b> has <b>{fmtIN(target.sheetsOnHand + item.sheetsOnHand)} sheets</b> and {item.sku} is gone from the list.
          </div>
        )}
        {err && <div className="mt-3 text-sm text-red-700">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            disabled={!target || merge.isPending}
            onClick={() => { if (target && confirm(`Merge ${item.sku} into ${target.sku}? ${item.sku} will be retired. This can't be undone.`)) merge.mutate(); }}
          >
            {merge.isPending ? 'Merging…' : target ? `Merge ${item.sku} into ${target.sku}` : 'Pick a SKU'}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, placeholder, disabled }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input disabled:bg-slate-50 disabled:text-slate-500" value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} />
    </div>
  );
}

/* ─────────────────── Issue to Job modal ─────────────────── */

/** A job card's cut size in inches, in the card's own order: "609.6 x 393.7" (mm) → "24 × 15.5 in". */
function cutInches(text: string | null | undefined): string {
  if (!parseCutSize(text)) return '';
  return `${(text ?? '').split(/\s*[xX×*]\s*/).map((p) => {
    const n = parseFloat(p);
    // mm to the nearest 0.05 in (711 mm reads 28, not 27.99); inches as typed
    return String(n > 60 ? Math.round((n / 25.4) * 20) / 20 : n);
  }).join(' × ')} in`;
}

interface JobInfo {
  found: boolean;
  jobId?: string;
  clientName?: string;
  productName?: string;
  poNumber?: string;
  poQty?: number;
  jobCardQty?: number;
  cutSize?: string;
  /** The job card's sheets, wastage included. */
  sheetsPlanned?: number;
  /** The GSM the party asked for (the product's Party GSM). */
  partyGsm?: string;
}

/**
 * Issue N sheets from a board to a specific Job Card.
 *
 * The operator types (or scans) the Job Card number.  On blur we hit
 * /api/paper-stock/job-info/:jobId to auto-fill Client, Product, PO No,
 * PO Qty, JC Qty, Cut Size and the card's sheets incl. wastage — everything
 * else is snapshot data the store doesn't need to re-type.  The quantity is
 * the store's own to type.  The board's rate is recorded on the issue by the
 * server, so the store isn't asked for a price.
 */
function IssueModal({ item, onClose, onSaved }: {
  item: PaperRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [jobId, setJobId] = useState('');
  const [jobInfo, setJobInfo] = useState<JobInfo | null>(null);
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'notfound' | 'ok'>('idle');
  const [qty, setQty] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // The job's cut size against this board: how the sheet is cut, and the
  // strip left over that is worth keeping as cutting stock.
  const cut = parseCutSize(jobInfo?.cutSize);
  const plan = cut && item.lengthIn && item.widthIn ? cutPlan(item.lengthIn, item.widthIn, cut.l, cut.w) : null;
  const stripKey = (l: number, w: number) => `${l}x${w}`;
  /** Strips ticked to keep, and a sheet count when not the sheets issued. */
  const [keep, setKeep] = useState<Record<string, { on: boolean; sheets: string }>>({});
  const chosen = (plan?.strips ?? [])
    .filter(st => keep[stripKey(st.l, st.w)]?.on)
    .map(st => {
      const typed = (keep[stripKey(st.l, st.w)]?.sheets ?? '').trim();
      return { key: stripKey(st.l, st.w), l: st.l, w: st.w, sheets: typed ? Number(typed) : Number(qty) * st.perSheet };
    });
  // Once the sheets are issued, a retry only saves the leftover not yet saved.
  const issuedRef = useRef(false);
  const savedRef = useRef(new Set<string>());
  const [issued, setIssued] = useState(false);

  const lookup = async () => {
    const v = jobId.trim();
    if (!v) { setJobInfo(null); setLookupState('idle'); return; }
    setLookupState('loading');
    try {
      const info = await api<JobInfo>(`/api/paper-stock/job-info/${encodeURIComponent(v)}`);
      if (info.found) {
        setJobInfo(info);
        setLookupState('ok');
        // Snap the input to the fully-qualified JC id the server resolved to.
        if (info.jobId) setJobId(info.jobId);
      } else {
        setJobInfo(null);
        setLookupState('notfound');
      }
    } catch {
      setJobInfo(null);
      setLookupState('notfound');
    }
  };

  const save = useMutation({
    mutationFn: async () => {
      const job = (jobInfo?.jobId || jobId).trim();
      if (!issuedRef.current) {
        await api<{ ok: true; newStock: number }>(
          `/api/paper-stock/${item.id}/issue`,
          {
            method: 'POST',
            body: JSON.stringify({
              qty: Number(qty),
              jobId: job,
              clientName: jobInfo?.clientName ?? null,
              productName: jobInfo?.productName ?? null,
              poNumber: jobInfo?.poNumber ?? null,
              poQty: jobInfo?.poQty ?? null,
              jobCardQty: jobInfo?.jobCardQty ?? null,
              cutSize: jobInfo?.cutSize ?? null,
              notes: notes.trim() || null,
            }),
          },
        );
        issuedRef.current = true;
        setIssued(true);
      }
      for (const st of chosen) {
        if (savedRef.current.has(st.key)) continue;
        await api(`/api/paper-stock/${item.id}/leftover`, {
          method: 'POST',
          body: JSON.stringify({ lengthIn: st.l, widthIn: st.w, sheets: st.sheets, jobId: job || null }),
        });
        savedRef.current.add(st.key);
      }
    },
    onSuccess: onSaved,
    onError: (e) => setErr(issuedRef.current
      ? `The sheets were issued, but the leftover was not saved to cutting stock: ${(e as Error).message}. Press Save leftover to try again, or Close.`
      : (e as Error).message),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    for (const st of chosen) {
      if (!Number.isInteger(st.sheets) || st.sheets <= 0) { setErr(`Enter how many ${st.l} × ${st.w} in leftover sheets to keep.`); return; }
    }
    if (issued) { save.mutate(); return; }
    const q = Number(qty);
    if (!jobId.trim()) { setErr('Job Card number is required.'); return; }
    if (!q || q <= 0) { setErr('Enter a valid quantity.'); return; }
    if (q > item.sheetsOnHand) {
      setErr(`Only ${item.sheetsOnHand} sheets on hand — cannot issue ${q}.`);
      return;
    }
    save.mutate();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        className="bg-white rounded-xl shadow-2xl p-6 max-w-xl w-full mx-4 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h2 className="text-lg font-semibold mb-1 text-brand">Issue paper to job</h2>
        <div className="text-xs text-slate-500 mb-4">
          <span className="font-semibold text-slate-700">{item.sku}</span>
          {item.paperType && <> · {item.paperType}</>}
          {item.lengthIn && item.widthIn && <> · {item.lengthIn} × {item.widthIn}</>}
          {item.gsm && <> · {item.gsm} GSM</>}
          {item.location && <> · Location: <span className="font-semibold text-slate-700">{item.location}</span></>}
          <> · On hand: <span className="font-semibold text-emerald-700">{item.sheetsOnHand.toLocaleString('en-IN')}</span> sheets</>
        </div>

        <div className="grid gap-3">
          <div>
            <label className="label">Job Card Number *</label>
            <input
              className="input"
              value={jobId}
              autoFocus
              placeholder="e.g. 81852 or VP/092026/81852"
              onChange={e => { setJobId(e.target.value); if (lookupState !== 'idle') setLookupState('idle'); }}
              onBlur={lookup}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); lookup(); } }}
            />
            {lookupState === 'loading' && <div className="text-[11px] text-slate-500 mt-1">Looking up…</div>}
            {lookupState === 'notfound' && <div className="text-[11px] text-red-600 mt-1">Job not found — check the number.</div>}
          </div>

          {jobInfo?.found && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-xs grid grid-cols-2 gap-x-4 gap-y-1.5">
              <div><span className="text-slate-500">Client:</span> <span className="font-semibold">{jobInfo.clientName || '—'}</span></div>
              <div><span className="text-slate-500">PO No:</span> <span className="font-semibold">{jobInfo.poNumber || '—'}</span></div>
              <div className="col-span-2"><span className="text-slate-500">Product:</span> <span className="font-semibold">{jobInfo.productName || '—'}</span></div>
              <div><span className="text-slate-500">PO Qty:</span> <span className="font-semibold">{jobInfo.poQty?.toLocaleString('en-IN') ?? '—'}</span></div>
              <div><span className="text-slate-500">JC Qty:</span> <span className="font-semibold">{jobInfo.jobCardQty?.toLocaleString('en-IN') ?? '—'}</span></div>
              {/* In inches, like the board sizes; the card may hold mm. */}
              <div><span className="text-slate-500">Cut Size:</span> <span className="font-semibold">{cutInches(jobInfo.cutSize) || jobInfo.cutSize || '—'}</span></div>
              <div><span className="text-slate-500">Party GSM:</span> <span className="font-semibold">{jobInfo.partyGsm || '—'}</span></div>
              <div>
                <span className="text-slate-500">Sheets incl. wastage:</span>{' '}
                <span className="font-semibold text-brand">{jobInfo.sheetsPlanned ? jobInfo.sheetsPlanned.toLocaleString('en-IN') : '—'}</span>
              </div>
            </div>
          )}

          <div>
            <label className="label">Quantity (sheets) *</label>
            <input
              type="number" min={1} step={1}
              className="input"
              value={qty}
              onChange={e => setQty(e.target.value)}
              placeholder="e.g. 500"
              disabled={issued}
            />
          </div>

          {jobInfo?.found && cut && item.lengthIn && item.widthIn && (
            !plan ? (
              <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-800">
                The job's cut size {cut.l} × {cut.w} in is bigger than this board ({item.lengthIn} × {item.widthIn} in). Check the board before issuing.
              </div>
            ) : (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs grid gap-2">
                <div>
                  <span className="text-slate-500">Cut plan:</span>{' '}
                  <span className="font-semibold">{plan.pieces} {plan.pieces === 1 ? 'piece' : 'pieces'} of {cut.l} × {cut.w} in</span>
                  {' '}from each {item.lengthIn} × {item.widthIn} sheet
                  {plan.pieces > 1 && <> ({plan.across} × {plan.down})</>}.
                </div>
                {plan.strips.length === 0 ? (
                  <div className="text-slate-500">
                    {plan.wasteSqIn > 0 ? `No leftover worth keeping — strips under ${MIN_STRIP_IN} in are trimmed off.` : 'No leftover — the sheet cuts exactly.'}
                  </div>
                ) : plan.strips.map(st => {
                  const k = stripKey(st.l, st.w);
                  const row = keep[k] ?? { on: false, sheets: '' };
                  const auto = Number(qty) > 0 ? Number(qty) * st.perSheet : null;
                  const done = savedRef.current.has(k);
                  return (
                    <div key={k} className="flex flex-wrap items-center gap-2">
                      <label className="flex items-center gap-1.5 font-semibold text-amber-900">
                        <input
                          type="checkbox"
                          checked={row.on}
                          disabled={done || save.isPending}
                          onChange={e => setKeep({ ...keep, [k]: { ...row, on: e.target.checked } })}
                        />
                        Add the leftover {st.l} × {st.w} in to cutting stock
                        {st.perSheet > 1 && <span className="font-normal text-slate-500">({st.perSheet} per sheet)</span>}
                      </label>
                      {row.on && (
                        <input
                          type="number" min={1} step={1}
                          className="input !w-28 !py-0.5 text-xs"
                          value={row.sheets}
                          placeholder={auto != null ? String(auto) : 'sheets'}
                          disabled={done}
                          onChange={e => setKeep({ ...keep, [k]: { ...row, sheets: e.target.value } })}
                          title="Sheets of leftover to keep (one per sheet issued unless changed)"
                        />
                      )}
                      {row.on && <span className="text-slate-500">{done ? 'saved' : 'sheets'}</span>}
                    </div>
                  );
                })}
              </div>
            )
          )}

          <div>
            <label className="label">Notes</label>
            <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" disabled={issued} />
          </div>
        </div>

        {err && <div className="text-red-700 text-sm mt-3">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={issued ? onSaved : onClose}>{issued ? 'Close' : 'Cancel'}</button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : issued ? 'Save leftover' : chosen.length ? 'Issue sheets + save leftover' : 'Issue sheets'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─────────────────── Receive (add stock) modal ─────────────────── */

interface OpenPo {
  poId: number;
  poNumber: string;
  poDate: string;
  vendorName: string;
  qty: number;
  uom: string;
  rate: number;
  receivedSheets: number;
}

function ReceiveModal({ item, preset, onClose, onSaved }: {
  item: PaperRow;
  /** Opened from a PO line: that PO, the sheets still due and the PO rate. */
  preset?: ReceivePreset;
  onClose: () => void;
  onSaved: () => void;
}) {
  // kg per sheet from the board's size and GSM; 0 when those aren't filled in.
  const kgPerSheet = sheetKg(item);
  /** A per-sheet rate as a per-kg one, for POs bought in sheets. */
  const perKgFromSheet = (perSheet: number) => (kgPerSheet > 0 ? r2(perSheet / kgPerSheet) : null);

  const [sheetsText, setSheetsText] = useState(preset?.qty ? String(preset.qty) : '');
  const [areaText, setAreaText] = useState(item.location ?? '');
  const [packetsText, setPacketsText] = useState('');
  const [sprText, setSprText] = useState(item.sheetPerRim != null ? String(item.sheetPerRim) : '');
  const [weightText, setWeightText] = useState('');
  const [rateText, setRateText] = useState(() => {
    if (preset?.ratePerKg != null && preset.ratePerKg > 0) return String(r2(preset.ratePerKg));
    if (preset?.rate != null && preset.rate > 0) return String(perKgFromSheet(preset.rate) ?? '');
    // Otherwise the board's own rate — per kg, the price last paid.
    return item.rate != null && item.rate > 0 ? String(item.rate) : '';
  });
  const [invoiceDate, setInvoiceDate] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [poNumber, setPoNumber] = useState(preset?.poNumber ?? '');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);

  // Open board POs that include this board, so a delivery can be booked
  // against the right one (the PO then shows it as received).
  const openPos = useQuery({
    queryKey: ['board-po-open-for', item.id],
    queryFn: () => api<{ rows: OpenPo[] }>(`/api/board-po/open-for/${item.id}`),
    retry: false,
  });
  const pos = openPos.data?.rows ?? [];
  function pickPo(n: string) {
    setPoNumber(n);
    const po = pos.find(p => p.poNumber === n);
    if (!po) return;
    if (po.uom === 'SHEETS') {
      const due = Math.max(0, po.qty - po.receivedSheets);
      if (!sheetsText.trim() && due) setSheetsText(String(due));
    }
    // The PO's price, as a rate per kg.
    const perKg = !(po.rate > 0) ? null
      : po.uom === 'KG' ? r2(po.rate)
      : po.uom === 'TON' ? r2(po.rate / 1000)
      : po.uom === 'SHEETS' ? perKgFromSheet(po.rate)
      : null;
    if (perKg != null) setRateText(String(perKg));
  }

  // Worked out live from what is typed: sheets from packets x sheet/rim when
  // left blank, weight from the board's size and GSM, value = weight x rate.
  const packets = readNumber(packetsText);
  const spr = readNumber(sprText);
  const sheetsTyped = readNumber(sheetsText);
  const sheetsAuto = packets != null && spr != null && packets > 0 && spr > 0 ? packets * spr : null;
  const sheets = sheetsTyped ?? sheetsAuto;
  const weightTyped = readNumber(weightText);
  const weightAuto = sheets != null && sheets > 0 && kgPerSheet > 0 ? r2(sheets * kgPerSheet) : null;
  const weight = weightTyped ?? weightAuto;
  const ratePerKg = readNumber(rateText);
  const value = weight != null && weight > 0 && ratePerKg != null && ratePerKg > 0 ? kgValue(weight, ratePerKg) : null;

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api(`/api/paper-stock/${item.id}/receive`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
    onSuccess: onSaved,
    onError: (e) => {
      const m = (e as Error).message;
      setErr(m === 'rate_required' ? 'Enter the rate per kg — a receipt is not saved without it.' : m);
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const unreadable = ([
      ['Quantity', sheetsTyped], ['No. of Packets', packets], ['Sheet/Rim', spr],
      ['Weight', weightTyped], ['Rate', ratePerKg],
    ] as const).find(([, v]) => v != null && (Number.isNaN(v) || v < 0));
    if (unreadable) {
      setErr(`${unreadable[0]}: ${Number.isNaN(unreadable[1]) ? "can't read that — use a number or =formula" : "can't be negative"}.`);
      return;
    }
    if (sheets == null || !(sheets > 0)) {
      setErr('Enter the quantity received in sheets, or the packets and sheet/rim.');
      return;
    }
    const qty = Math.round(sheets);
    if (Math.abs(sheets - qty) > 1e-6) { setErr(`Quantity must be a whole number of sheets (it comes to ${fmtQty(sheets)}).`); return; }
    if (spr != null && Math.abs(spr - Math.round(spr)) > 1e-6) { setErr('Sheet/Rim must be a whole number.'); return; }
    // The rate values the stock: a receipt is not saved without it.
    if (ratePerKg == null || !(ratePerKg > 0)) { setErr('Enter the rate per kg — a receipt is not saved without it.'); return; }
    save.mutate({
      qty,
      packets: packets ?? null,
      sheetPerRim: spr != null ? Math.round(spr) : null,
      weightKg: weight != null ? r2(weight) : null,
      ratePerKg: ratePerKg ?? null,
      poNumber: poNumber.trim() || null,
      invoiceNumber: invoiceNumber.trim() || null,
      invoiceDate: invoiceDate || null,
      storageArea: areaText.trim() || null,
      notes: notes.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <form
        className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full mx-4 max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h2 className="text-lg font-semibold mb-3 text-emerald-700">Receive stock (In)</h2>
        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-700 mb-4">
          <div>
            <span className="font-semibold">{item.sku}</span>
            {item.paperType && <> · {item.paperType}</>}
            {item.lengthIn != null && <> · Length: <b>{item.lengthIn}</b></>}
            {item.widthIn != null && <> · Width: <b>{item.widthIn}</b></>}
            {item.gsm != null && <> · GSM: <b>{item.gsm}</b></>}
            {item.brand && <> · {item.brand}</>}
            {item.location && <> · {item.location}</>}
          </div>
          <div>Current stock: <b>{fmtIN(item.sheetsOnHand)} sheets</b></div>
        </div>

        {pos.length > 0 && (
          <div className="mb-3">
            <label className="label">Against board PO</label>
            <select
              className="input"
              value={pos.some(p => p.poNumber === poNumber) ? poNumber : ''}
              onChange={e => pickPo(e.target.value)}
            >
              <option value="">— Not against an open PO —</option>
              {pos.map(p => (
                <option key={p.poId} value={p.poNumber}>
                  {p.poNumber} · {p.vendorName} · {qtyText(p.qty)} {p.uom}
                  {p.receivedSheets ? ` · ${qtyText(p.receivedSheets)} sheets in` : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <div>
            <label className="label">Quantity received (sheets) *</label>
            <input className="input" inputMode="decimal" autoFocus
              value={sheetsText} onChange={e => setSheetsText(e.target.value)}
              placeholder={sheetsAuto != null ? `Auto: ${fmtQty(sheetsAuto)}` : '=formula OK'} />
            <Worked text={sheetsText} value={sheetsTyped} />
            {sheetsTyped == null && sheetsAuto != null && (
              <div className="text-[11px] text-slate-500 mt-0.5">From packets: {fmtQty(packets!)} × {fmtQty(spr!)} = {fmtQty(sheetsAuto)}</div>
            )}
          </div>
          <div>
            <label className="label">Storage Area</label>
            <input className="input" value={areaText} onChange={e => setAreaText(e.target.value)} placeholder="e.g. H BLOCK" />
          </div>

          <div>
            <label className="label">No. of Packets</label>
            <input className="input" inputMode="decimal" value={packetsText} onChange={e => setPacketsText(e.target.value)} placeholder="e.g. 5.5 or =formula" />
            <Worked text={packetsText} value={packets} />
          </div>
          <div>
            <label className="label">Sheet/Rim</label>
            <input className="input" inputMode="decimal" value={sprText} onChange={e => setSprText(e.target.value)} placeholder="e.g. 100" />
            <Worked text={sprText} value={spr} />
          </div>

          <div>
            <label className="label">Weight (kg)</label>
            <input className="input" inputMode="decimal" value={weightText} onChange={e => setWeightText(e.target.value)}
              placeholder={weightAuto != null ? `Auto: ${fmtQty(weightAuto)}` : 'Auto-calc or =formula'} />
            <Worked text={weightText} value={weightTyped} unit=" kg" />
            {weightTyped == null && weightAuto != null && (
              <div className="text-[11px] text-slate-500 mt-0.5">Auto from {item.lengthIn} × {item.widthIn} in, {item.gsm} GSM</div>
            )}
            {weightTyped == null && kgPerSheet <= 0 && (
              <div className="text-[11px] text-amber-700 mt-0.5">Add Length, Width and GSM to this board to auto-calculate</div>
            )}
          </div>
          <div>
            <label className="label">Rate (per kg) *</label>
            <input className="input" inputMode="decimal" value={rateText} onChange={e => setRateText(e.target.value)} placeholder="e.g. 46.50 or =formula" />
            <Worked text={rateText} value={ratePerKg} />
          </div>

          <div>
            <label className="label">Value (Auto: Weight x Rate)</label>
            <input className="input bg-slate-50" readOnly tabIndex={-1} value={value != null ? `₹ ${fmtIN(value, 2)}` : ''} />
          </div>
          <div>
            <label className="label">Invoice Date</label>
            <input type="date" className="input" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} />
          </div>

          <div>
            <label className="label">Invoice Number</label>
            <input className="input" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="e.g. INV-2024-001" />
          </div>
          <div>
            <label className="label">PO Number</label>
            <input className="input" value={poNumber} onChange={e => setPoNumber(e.target.value)} placeholder="e.g. PO-12345" />
          </div>

          <div className="col-span-2">
            <label className="label">Notes</label>
            <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        {err && <div className="text-red-700 text-sm mt-3">{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary bg-emerald-600 hover:bg-emerald-700" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Add stock'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ─────────────────── Stock Count (physical count) ─────────────────── */

/** One board's count as typed, and the system stock when it was typed. */
interface CountLine { packets: string; loose: string; expected: number }
interface CountDraft { countedOn: string; note: string; lines: Record<string, CountLine> }

const COUNT_DRAFT_KEY = 'board-reconcile-draft';
/** Today's date where the browser is (not UTC), as YYYY-MM-DD. */
const localToday = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};
const emptyDraft = (): CountDraft => ({ countedOn: localToday(), note: '', lines: {} });
function loadCountDraft(): CountDraft {
  try {
    const d = JSON.parse(localStorage.getItem(COUNT_DRAFT_KEY) ?? 'null');
    if (d && typeof d === 'object' && d.lines && typeof d.countedOn === 'string') return d as CountDraft;
  } catch { /* storage blocked or bad data — start a fresh count */ }
  return emptyDraft();
}

const signed = (n: number, digits = 0) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtIN(Math.abs(n), digits)}`;
/** Rupees with the sign in front of the ₹: −₹390.24. */
const signedRs = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}₹${fmtIN(Math.abs(n), 2)}`;

/**
 * Physical stock count.  The store enters what is physically there —
 * packets x the board's sheet/rim, plus loose sheets — and each row shows
 * the difference from the system stock.  The system figure is the one on
 * hand when that row's count was first typed, so an issue or receipt after
 * that isn't mistaken for a difference; posting applies each difference to
 * today's stock as an ADJUST line in the board's History.
 *
 * The count is kept in this browser until it is posted or cleared, so a long
 * count survives a refresh.
 */
function StockCountTab({ onPosted }: { onPosted: () => void }) {
  const all = useQuery({
    queryKey: ['paper-stock', 'reconcile'],
    queryFn: () => api<{ rows: PaperRow[] }>('/api/paper-stock?sort=sku'),
  });
  const [draft, setDraft] = useState<CountDraft>(loadCountDraft);
  const [search, setSearch] = useState('');
  const [onlyCounted, setOnlyCounted] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    try { localStorage.setItem(COUNT_DRAFT_KEY, JSON.stringify(draft)); } catch { /* storage off: the count still works for this visit */ }
  }, [draft]);

  const boards = all.data?.rows ?? [];

  /** A row's count worked out: whole sheets counted, the difference, or why it can't be read. */
  const calc = (r: PaperRow) => {
    const l = draft.lines[String(r.id)];
    const none = { counted: null as number | null, diff: null as number | null, bad: null as string | null, moved: false };
    if (!l) return none;
    const spr = Number(r.sheetPerRim) || 0;
    const p = readNumber(l.packets);
    const loose = readNumber(l.loose);
    for (const v of [p, loose]) {
      if (v != null && (Number.isNaN(v) || v < 0)) return { ...none, bad: "Can't read a count — use a number or =formula" };
    }
    if (p == null && loose == null) return none;
    if (p != null && p > 0 && spr <= 0) return { ...none, bad: 'No sheet/rim on this board — count loose sheets' };
    const total = (p ?? 0) * spr + (loose ?? 0);
    if (Math.abs(total - Math.round(total)) > 1e-6) return { ...none, bad: `Comes to ${fmtQty(total)} sheets — must be whole` };
    const counted = Math.round(total);
    return { counted, diff: counted - l.expected, bad: null, moved: l.expected !== r.sheetsOnHand };
  };

  /** Rupees for a difference in sheets, by weight x the board's rate per kg. */
  const diffValue = (r: PaperRow, diff: number): number | null => {
    const kg = sheetKg(r);
    const rate = Number(r.rate) || 0;
    if (kg <= 0 || rate <= 0 || diff === 0) return diff === 0 ? 0 : null;
    return Math.sign(diff) * kgValue(r2(Math.abs(diff) * kg), rate);
  };

  function setLine(r: PaperRow, field: 'packets' | 'loose', value: string) {
    setMsg(null);
    setDraft(d => {
      const lines = { ...d.lines };
      const key = String(r.id);
      const next = { ...(lines[key] ?? { packets: '', loose: '', expected: r.sheetsOnHand }), [field]: value };
      if (!next.packets.trim() && !next.loose.trim()) delete lines[key];
      else lines[key] = next;
      return { ...d, lines };
    });
  }

  const term = search.trim().toLowerCase();
  const shown = boards.filter(r => {
    if (onlyCounted && !draft.lines[String(r.id)]) return false;
    if (!term) return true;
    return [r.sku, r.paperType, r.brand, r.location, r.gsm, r.lengthIn, r.widthIn]
      .some(v => v != null && String(v).toLowerCase().includes(term));
  });

  const counted = boards.map(r => ({ r, c: calc(r) })).filter(x => x.c.counted != null);
  const differing = counted.filter(x => x.c.diff !== 0);
  const unreadable = boards.filter(r => calc(r).bad);
  const netSheets = differing.reduce((s, x) => s + (x.c.diff ?? 0), 0);
  const values = differing.map(x => diffValue(x.r, x.c.diff!));
  const netValue = values.every(v => v != null) ? r2(values.reduce((s, v) => s + (v ?? 0), 0)) : null;

  const post = useMutation({
    mutationFn: (lines: { id: number; expectedSheets: number; countedSheets: number; countedPackets: number | null; looseSheets: number | null }[]) =>
      api<{ adjusted: number }>('/api/paper-stock/reconcile', {
        method: 'POST',
        body: JSON.stringify({ countedOn: draft.countedOn, note: draft.note.trim() || null, lines }),
      }),
    onSuccess: (res) => {
      setDraft(emptyDraft());
      setMsg({ ok: true, text: `Posted ${res.adjusted} adjustment${res.adjusted === 1 ? '' : 's'} — stock on hand is updated, and each is in the board's History.` });
      onPosted();
    },
    onError: (e) => {
      const m = (e as Error).message;
      const neg = /^would_go_negative:(.+):(-?\d+):(-?\d+)$/.exec(m);
      setMsg({ ok: false, text: neg
        ? `Nothing posted: ${neg[1]} has ${fmtIN(Number(neg[2]))} sheets on hand now, and a difference of ${signed(Number(neg[3]))} would take it below zero. Recount it.`
        : m.startsWith('board_not_found') ? 'Nothing posted: a counted board has been deleted since. Clear its count and post again.'
        : `Nothing posted: ${m}` });
    },
  });

  function onPost() {
    setMsg(null);
    if (unreadable.length) {
      setMsg({ ok: false, text: `Fix the count for ${unreadable.map(r => r.sku).join(', ')} first.` });
      return;
    }
    if (!differing.length) {
      setMsg({ ok: false, text: counted.length ? 'Every counted board matches the system — nothing to post.' : 'Nothing counted yet.' });
      return;
    }
    const list = differing.map(x => `${x.r.sku} ${signed(x.c.diff!)}`).join(', ');
    if (!window.confirm(`Post ${differing.length} adjustment${differing.length === 1 ? '' : 's'}?\n\n${list}\n\nNet ${signed(netSheets)} sheets. Stock on hand changes for these boards, and each difference is recorded in its History.`)) return;
    post.mutate(differing.map(({ r, c }) => {
      const l = draft.lines[String(r.id)]!;   // counted rows always have one
      const p = readNumber(l.packets);
      const loose = readNumber(l.loose);
      return {
        id: r.id,
        expectedSheets: l.expected,
        countedSheets: c.counted!,
        countedPackets: p != null && Number(r.sheetPerRim) > 0 ? p : null,
        looseSheets: loose != null ? Math.round(loose) : null,
      };
    }));
  }

  function clearCount() {
    if (Object.keys(draft.lines).length && !window.confirm('Clear every count entered on this sheet?')) return;
    setDraft(emptyDraft());
    setMsg(null);
  }

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-2">
        <div>
          <label className="label">Count date</label>
          <input type="date" className="input" value={draft.countedOn} max={localToday()}
            onChange={e => setDraft(d => ({ ...d, countedOn: e.target.value || localToday() }))} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="label">Note on every adjustment</label>
          <input className="input" value={draft.note} onChange={e => setDraft(d => ({ ...d, note: e.target.value }))}
            placeholder="e.g. Month-end count, H BLOCK" maxLength={200} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="label">Find</label>
          <input className="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="SKU, paper, brand, size, location" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 whitespace-nowrap pb-2">
          <input type="checkbox" checked={onlyCounted} onChange={e => setOnlyCounted(e.target.checked)} />
          Only counted
        </label>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Enter what is physically there — packets (× the board's sheet/rim) and loose sheets; a formula like =12*5+3 works.
        The difference is against the system stock when you type the count, and nothing changes until you post.
        The count stays in this browser until then.
      </p>

      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <Th>SKU</Th>
              <Th>Paper</Th>
              <Th>Size</Th>
              <Th numeric>GSM</Th>
              <Th>Brand</Th>
              <Th>Location</Th>
              <Th numeric>Sheet/Rim</Th>
              <Th numeric>System</Th>
              <Th>Counted packets</Th>
              <Th>Loose sheets</Th>
              <Th numeric>Counted</Th>
              <Th numeric>Difference</Th>
              <Th numeric>Value</Th>
            </tr>
          </thead>
          <tbody>
            {all.isLoading ? (
              <tr><td colSpan={13} className="p-6 text-center text-slate-400">Loading…</td></tr>
            ) : shown.length === 0 ? (
              <tr><td colSpan={13} className="p-6 text-center text-slate-400">{boards.length ? 'No boards match.' : 'No boards yet.'}</td></tr>
            ) : shown.map(r => {
              const l = draft.lines[String(r.id)];
              const c = calc(r);
              const spr = Number(r.sheetPerRim) || 0;
              const v = c.diff != null ? diffValue(r, c.diff) : null;
              const tone = c.diff == null || c.diff === 0 ? 'text-slate-500' : c.diff > 0 ? 'text-emerald-700' : 'text-red-600';
              return (
                <tr key={r.id} className={`border-t border-slate-100 align-top ${l ? 'bg-sky-50/50' : ''}`}>
                  <td className="px-3 py-1.5 font-mono font-semibold text-brand">{r.sku}</td>
                  <td className="px-3 py-1.5">{r.paperType || '—'}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">{r.lengthIn != null && r.widthIn != null ? `${r.lengthIn} × ${r.widthIn}` : '—'}</td>
                  <td className="px-3 py-1.5 text-right">{r.gsm ?? '—'}</td>
                  <td className="px-3 py-1.5">{r.brand || '—'}</td>
                  <td className="px-3 py-1.5">{r.location || '—'}</td>
                  <td className="px-3 py-1.5 text-right">{spr || '—'}</td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    <div className="font-semibold">{fmtIN(r.sheetsOnHand)}</div>
                    {spr > 0 && <div className="text-[10px] text-slate-400">≈ {fmtQty(r.sheetsOnHand / spr)} pkt</div>}
                  </td>
                  <td className="px-2 py-1">
                    <input className="input w-24 px-2 py-1 text-xs" inputMode="decimal" value={l?.packets ?? ''}
                      disabled={spr <= 0} title={spr <= 0 ? 'No sheet/rim on this board — count loose sheets' : undefined}
                      onChange={e => setLine(r, 'packets', e.target.value)} placeholder={spr > 0 ? 'packets' : '—'} />
                    {l && <Worked text={l.packets} value={readNumber(l.packets)} />}
                  </td>
                  <td className="px-2 py-1">
                    <input className="input w-24 px-2 py-1 text-xs" inputMode="decimal" value={l?.loose ?? ''}
                      onChange={e => setLine(r, 'loose', e.target.value)} placeholder="sheets" />
                    {l && <Worked text={l.loose} value={readNumber(l.loose)} />}
                  </td>
                  <td className="px-3 py-1.5 text-right font-semibold">
                    {c.counted != null ? fmtIN(c.counted) : c.bad ? <span className="text-[11px] font-normal text-red-600">{c.bad}</span> : '—'}
                  </td>
                  <td className={`px-3 py-1.5 text-right whitespace-nowrap font-semibold ${tone}`}>
                    {c.diff != null ? (
                      <>
                        <div>{c.diff === 0 ? 'Matches' : signed(c.diff)}</div>
                        {c.diff !== 0 && spr > 0 && <div className="text-[10px] font-normal">{signed(c.diff / spr, 2)} pkt</div>}
                        {c.moved && (
                          <div className="text-[10px] font-normal text-amber-700" title="Posting applies the difference to today's stock, keeping that movement">
                            system was {fmtIN(l!.expected)} at count
                          </div>
                        )}
                      </>
                    ) : '—'}
                  </td>
                  <td className={`px-3 py-1.5 text-right whitespace-nowrap ${tone}`}>
                    {c.diff == null || c.diff === 0 ? '—' : v == null ? <span className="text-[10px] text-slate-400">no rate / size</span> : signedRs(v)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-slate-600">
          Counted <b>{counted.length}</b> of {boards.length} board{boards.length === 1 ? '' : 's'}
          {' · '}<b>{differing.length}</b> with a difference
          {differing.length > 0 && (
            <> · net <b className={netSheets < 0 ? 'text-red-600' : netSheets > 0 ? 'text-emerald-700' : ''}>{signed(netSheets)}</b> sheets
              {netValue != null && <> ({signedRs(netValue)})</>}</>
          )}
        </div>
        <div className="flex gap-2">
          <button type="button" className="btn-ghost" onClick={clearCount} disabled={post.isPending}>Clear count</button>
          <button type="button" className="btn-primary" onClick={onPost} disabled={post.isPending || differing.length === 0}>
            {post.isPending ? 'Posting…' : `Post ${differing.length} adjustment${differing.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
      {msg && <div className={`mt-2 text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`}>{msg.text}</div>}
    </div>
  );
}

/* ─────────────────── Transaction history modal ─────────────────── */

interface Txn {
  id: number; type: string; sheets: number;
  jobId: string; clientName: string; productName: string;
  poNumber: string; poQty: number | null; jobCardQty: number | null;
  cutSize: string; rate: number | null; notes: string;
  createdOn: string; byName: string;
  // A reversal row carries reversesTxnId → the original it undid.
  // An original that HAS been reversed carries reversedByTxnId → the reversal.
  reversesTxnId: number | null;
  reversedByTxnId: number | null;
  /** This user may reverse it now (Purchase: within an hour of the movement). */
  canReverse?: boolean;
  // What was booked in on a receipt; null / '' on other rows and older ones.
  packets?: number | null;
  sheetPerRim?: number | null;
  weightKg?: number | null;
  ratePerKg?: number | null;
  value?: number | null;
  invoiceNumber?: string;
  invoiceDate?: string | null;
  storageArea?: string;
  /** Why a job's paper was reconciled (extra used / returned). */
  reason?: string;
  /** A leftover receipt: the job whose issue it was cut from, and that board. */
  leftoverOf?: { jobId: string; fromSku: string } | null;
}

function HistoryModal({ item, onClose }: { item: PaperRow; onClose: () => void }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const superAdmin = user?.rollId === ROLE.SuperAdmin;
  const canManage = can(user, 'MANAGE_INVENTORY');
  const { data, isLoading } = useQuery({
    queryKey: ['paper-stock-history', item.id],
    queryFn: () => api<{ rows: Txn[] }>(`/api/paper-stock/${item.id}/history`),
  });

  const reverse = useMutation({
    mutationFn: (txnId: number) => api(`/api/paper-stock/txns/${txnId}/reverse`, {
      method: 'POST',
      body: JSON.stringify({}),
    }),
    onSuccess: () => {
      // Refresh both the history feed AND the dashboard so Sheets on Hand updates.
      qc.invalidateQueries({ queryKey: ['paper-stock-history', item.id] });
      qc.invalidateQueries({ queryKey: ['paper-stock'] });
      qc.invalidateQueries({ queryKey: ['paper-stock-summary'] });
    },
    onError: (e) => alert(reverseError(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl p-6 max-w-5xl w-full mx-4 max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-brand">Transaction history</h2>
            <div className="text-xs text-slate-500 mt-0.5">
              <span className="font-semibold text-slate-700">{item.sku}</span>
              {item.paperType && <> · {item.paperType}</>}
              {item.lengthIn && item.widthIn && <> · {item.lengthIn} × {item.widthIn}</>}
              {item.gsm && <> · {item.gsm} GSM</>}
              {item.location && <> · Location: <span className="font-semibold text-slate-700">{item.location}</span></>}
              <> · Currently on hand: <span className="font-semibold text-emerald-700">{item.sheetsOnHand.toLocaleString('en-IN')}</span></>
            </div>
          </div>
          <button className="btn-ghost text-sm" onClick={onClose}>Close</button>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Date</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Type</th>
                <th className="text-right px-3 py-2 font-medium text-slate-600">Sheets</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Job / PO</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Client / Product</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Invoice · Weight · Value</th>
                <th className="text-right px-3 py-2 font-medium text-slate-600">Rate</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">By</th>
                <th className="text-left px-3 py-2 font-medium text-slate-600">Notes</th>
                <th className="text-right px-3 py-2 font-medium text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={10} className="p-6 text-center text-slate-400">Loading…</td></tr>
              ) : (data?.rows ?? []).length === 0 ? (
                <tr><td colSpan={10} className="p-6 text-center text-slate-400">No transactions yet.</td></tr>
              ) : data!.rows.map(t => {
                const isIn = t.sheets > 0;
                const isReversal = t.reversesTxnId != null;
                const isReversed = t.reversedByTxnId != null;
                // Faded strike-through on the original that got reversed so
                // the operator sees at a glance which numbers no longer
                // count toward on-hand stock.
                const rowCls = isReversed
                  ? 'text-slate-400 line-through opacity-70'
                  : isReversal
                    ? 'bg-amber-50/60'
                    : '';
                return (
                  <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 ${rowCls}`}>
                    <td className="px-3 py-1.5 whitespace-nowrap text-slate-600">
                      {new Date(t.createdOn).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                        isReversal ? 'bg-amber-100 text-amber-800'
                          : isIn ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {TXN_LABEL[t.type] ?? t.type}
                      </span>
                      {isReversed && (
                        <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-600" title={`Reversed by txn #${t.reversedByTxnId}`}>
                          REVERSED
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-1.5 text-right font-semibold ${isIn ? 'text-emerald-700' : 'text-red-700'}`}>
                      {isIn ? '+' : ''}{t.sheets.toLocaleString('en-IN')}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs">
                      {t.leftoverOf && (
                        <div className="font-sans text-[10px] font-semibold uppercase text-sky-700">
                          Leftover of{t.leftoverOf.fromSku ? ` ${t.leftoverOf.fromSku}` : ''} issue
                        </div>
                      )}
                      {t.jobId && <div>{t.jobId}</div>}
                      {t.poNumber && <div className="text-slate-500">{t.poNumber}</div>}
                      {isReversal && t.reversesTxnId != null && (
                        <div className="text-amber-700 text-[10px] mt-0.5">↺ reverses #{t.reversesTxnId}</div>
                      )}
                    </td>
                    <td className="px-3 py-1.5 max-w-[220px]">
                      {t.clientName && <div className="font-medium truncate">{t.clientName}</div>}
                      {t.productName && <div className="text-slate-500 text-[11px] truncate">{t.productName}</div>}
                      {t.cutSize && <div className="text-slate-400 text-[10px]">Cut: {t.cutSize}</div>}
                    </td>
                    <td className="px-3 py-1.5 text-[11px] text-slate-600 whitespace-nowrap">
                      {(t.invoiceNumber || t.invoiceDate) && (
                        <div>
                          Inv {t.invoiceNumber || '—'}
                          {t.invoiceDate && <> · {new Date(t.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</>}
                        </div>
                      )}
                      {t.weightKg != null && (
                        <div>
                          {fmtIN(t.weightKg, 2)} kg
                          {t.value != null && <span className="font-semibold text-amber-700"> · ₹{fmtIN(t.value, 2)}</span>}
                        </div>
                      )}
                      {t.packets != null && (
                        <div className="text-slate-400">{t.packets} pkt{t.sheetPerRim ? ` × ${t.sheetPerRim}` : ''}</div>
                      )}
                      {t.storageArea && <div className="text-slate-400">{t.storageArea}</div>}
                      {!t.invoiceNumber && !t.invoiceDate && t.weightKg == null && t.packets == null && !t.storageArea && '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {(t.ratePerKg ?? t.rate) == null ? '—' : `₹${t.ratePerKg ?? t.rate}/kg`}
                    </td>
                    <td className="px-3 py-1.5 text-slate-600">{t.byName || '—'}</td>
                    <td className="px-3 py-1.5 text-slate-500 max-w-[200px]" title={[t.reason, t.notes].filter(Boolean).join(' — ')}>
                      {t.reason && <div className="font-medium text-slate-700 truncate">{t.reason}</div>}
                      <div className="truncate">{t.notes || (t.reason ? '' : '—')}</div>
                    </td>
                    <td className="px-3 py-1.5 text-right whitespace-nowrap">
                      {canManage && !isReversal && !isReversed && (t.canReverse ?? true) && (
                        <button
                          className="text-red-600 hover:underline text-[11px] font-medium disabled:text-slate-300 disabled:no-underline"
                          disabled={reverse.isPending}
                          onClick={() => {
                            const sign = t.sheets < 0 ? '+' : '-';
                            const absQty = Math.abs(t.sheets).toLocaleString('en-IN');
                            const msg = `Reverse this ${t.type.toLowerCase()} of ${absQty} sheets?\n\n` +
                              `This will ${sign === '+' ? 'ADD BACK' : 'REMOVE'} ${absQty} sheets from stock and leave both rows in history as an audit trail.`;
                            if (window.confirm(msg)) reverse.mutate(t.id);
                          }}
                          title="Reverse this transaction (writes a mirror row, keeps both for audit)"
                        >
                          {reverse.isPending ? '…' : 'Reverse'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {canManage && (
          <div className="text-[11px] text-slate-500 mt-2">
            ⓘ Reverse writes a mirror row — the original stays as history with a strike-through so the audit trail is intact.
            {!superAdmin && ' You can reverse a movement within 1 hour of it being made; after that, ask Super Admin.'}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Reports on the board that went to cutting: the consumption sheet (job-wise
 * board cut, its weight and value) and the cutting department's daily sheet.
 */
function BoardReportsTab() {
  const { user } = useAuth();
  // VIEW_BOARDS alone (the Planner): the Stock Movement report only - the
  // others are the cutting desk's and Purchase's.
  const boardsOnly = !can(user, 'VIEW_INVENTORY') && can(user, 'VIEW_BOARDS');
  const [report, setReport] = useState<'movement' | 'consumption' | 'daily'>('movement');
  const pill = (on: boolean) => `px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${on ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`;
  return (
    <div>
      <div className="inline-flex bg-slate-100 rounded-lg p-1 mb-4">
        <button onClick={() => setReport('movement')} className={pill(report === 'movement')}>Stock Movement</button>
        {!boardsOnly && <>
          <button onClick={() => setReport('consumption')} className={pill(report === 'consumption')}>Consumption</button>
          <button onClick={() => setReport('daily')} className={pill(report === 'daily')}>Daily Sheet</button>
        </>}
      </div>
      {report === 'movement' || boardsOnly ? <BoardMovementReport />
        : report === 'consumption' ? <ConsumptionTab /> : <DailySheetTab canRecord={can(user, 'RECORD_CUTTING')} />}
    </div>
  );
}

interface SheetRequest {
  id: number; jobId: string; paperStockId: number; sku?: string; boardSpec: string; requestedSheets: number;
  currentStock: number; reason: string; requestedOn: string; requestedBy: string | null;
}

/** An approve / reject refusal in words. */
function requestError(e: unknown): string {
  const m = (e as Error)?.message ?? '';
  const short = /^insufficient_stock:(\d+):(\d+)$/.exec(m);
  if (short) return `Not enough stock: ${Number(short[1]).toLocaleString('en-IN')} sheets on hand, ${Number(short[2]).toLocaleString('en-IN')} asked for.`;
  if (m === 'already_resolved') return 'Someone has already approved or rejected this request.';
  if (m === 'board_not_found') return 'That board is no longer in stock (deleted or merged).';
  return `Could not save: ${m}`;
}

/**
 * The cutting desk's requests for more sheets.  Approve issues the sheets to
 * the job (MANAGE_INVENTORY — Purchase, Super Admin); a view-only role sees
 * the list without the buttons.
 */
function RequestsTab() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canManage = can(user, 'MANAGE_INVENTORY');
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['cutting-requests', 'pending'],
    queryFn: () => api<{ rows: SheetRequest[] }>('/api/cutting/requests?status=PENDING').then(r => r.rows),
  });
  const done = () => {
    qc.invalidateQueries({ queryKey: ['cutting-requests'] });
    qc.invalidateQueries({ queryKey: ['paper-stock'] });
  };

  const [approving, setApproving] = useState<SheetRequest | null>(null);
  const approve = useMutation({
    // boardId: issue from that board instead of the one asked for.
    mutationFn: ({ id, boardId }: { id: number; boardId?: number }) => api(`/api/cutting/requests/${id}/approve`, {
      method: 'POST', body: JSON.stringify(boardId ? { paperStockId: boardId } : {}),
    }),
    onSuccess: () => { setApproving(null); done(); },
    onError: (e) => { alert(requestError(e)); done(); },
  });

  const reject = useMutation({
    mutationFn: (id: number) => api(`/api/cutting/requests/${id}/reject`, { method: 'POST' }),
    onSuccess: done,
    onError: (e) => { alert(requestError(e)); done(); },
  });

  if (isLoading) return <div className="text-slate-500">Loading...</div>;
  if (isError) return <div className="text-red-600">Could not load the requests: {(error as Error).message}</div>;

  const rows = data ?? [];

  return (
    <div className="bg-white rounded shadow-sm overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
        <h3 className="font-semibold text-slate-800">Pending Sheet Requests</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="text-slate-500 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Job No</th>
              <th className="px-4 py-3">Board Spec</th>
              <th className="px-4 py-3 text-right">Requested</th>
              <th className="px-4 py-3 text-right text-brand-600">In Stock</th>
              <th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Requested by</th>
              {canManage && <th className="px-4 py-3 text-center">Action</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.id}>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{new Date(r.requestedOn).toLocaleDateString()}</td>
                <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap" title={r.jobId}>{r.jobId.slice(r.jobId.lastIndexOf('/') + 1)}</td>
                <td className="px-4 py-3 text-slate-600 min-w-[200px]">
                  {r.sku && <span className="font-mono text-xs text-slate-500 mr-2">{r.sku}</span>}{r.boardSpec || '—'}
                </td>
                <td className="px-4 py-3 text-right font-medium text-slate-900 whitespace-nowrap">{r.requestedSheets.toLocaleString()}</td>
                <td className={`px-4 py-3 text-right font-medium whitespace-nowrap ${r.paperStockId !== 0 && r.currentStock < r.requestedSheets ? 'text-red-600' : 'text-brand-600'}`}>
                  {r.paperStockId === 0 ? '—' : r.currentStock.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-slate-600 min-w-[200px]">{r.reason}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.requestedBy || '—'}</td>
                {canManage && <td className="px-4 py-3 text-center whitespace-nowrap">
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={() => setApproving(r)}
                    disabled={approve.isPending || reject.isPending}
                    className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100 disabled:opacity-50 text-xs font-medium transition-colors"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => { if (window.confirm('Reject this request?')) reject.mutate(r.id); }}
                    disabled={approve.isPending || reject.isPending}
                    className="px-3 py-1 bg-red-50 text-red-700 rounded hover:bg-red-100 disabled:opacity-50 text-xs font-medium transition-colors"
                  >
                    Reject
                  </button>
                </div>
              </td>}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={canManage ? 8 : 7} className="px-4 py-8 text-center text-slate-500">No pending requests.</td></tr>
          )}
        </tbody>
      </table>
      </div>
      {approving && (
        <ApproveRequestModal
          req={approving}
          busy={approve.isPending}
          onClose={() => setApproving(null)}
          onApprove={(boardId) => approve.mutate({ id: approving.id, boardId })}
        />
      )}
    </div>
  );
}

interface BoardPick { id: number; sku: string; paperType: string; brand: string; gsm: number | null; lengthIn: number | null; widthIn: number | null; sheetsOnHand: number }

/**
 * Approve a sheet request: from the board asked for, or - when that one is
 * out of stock, or none was named - from another board the store picks.
 */
function ApproveRequestModal({ req, busy, onClose, onApprove }: {
  req: SheetRequest; busy: boolean; onClose: () => void; onApprove: (boardId?: number) => void;
}) {
  const hasBoard = req.paperStockId !== 0;
  const short = hasBoard && req.currentStock < req.requestedSheets;
  // Out of stock or no board named: start on the picker.
  const [other, setOther] = useState(short || !hasBoard);
  const [search, setSearch] = useState('');
  const [pick, setPick] = useState<BoardPick | null>(null);
  const boards = useQuery({
    queryKey: ['paper-stock', 'pick', search],
    queryFn: () => api<{ rows: BoardPick[] }>(`/api/paper-stock?inStockOnly=1&q=${encodeURIComponent(search.trim())}`).then(r => r.rows),
    enabled: other,
  });
  const list = (boards.data ?? []).filter((b) => b.id !== req.paperStockId).slice(0, 40);
  const spec = (b: BoardPick) => [b.paperType, b.gsm ? `${b.gsm}GSM` : '', b.lengthIn != null && b.widthIn != null ? `${b.lengthIn}x${b.widthIn}` : '', b.brand].filter(Boolean).join(' ');
  const enough = other ? !!pick && pick.sheetsOnHand >= req.requestedSheets : !short;
  const jobNo = req.jobId.slice(req.jobId.lastIndexOf('/') + 1);

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-800">Approve request — job {jobNo}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
            <div className="text-slate-900"><span className="font-semibold">{req.requestedSheets.toLocaleString()}</span> sheets asked for</div>
            <div className="text-slate-600 mt-1">
              Board asked for: {hasBoard ? <>{req.sku && <span className="font-mono text-xs mr-1">{req.sku}</span>}{req.boardSpec}</> : 'none named'}
              {hasBoard && <> · <span className={short ? 'text-red-600 font-medium' : 'text-emerald-700 font-medium'}>{req.currentStock.toLocaleString()} in stock</span></>}
            </div>
            {req.reason && <div className="text-xs text-slate-500 mt-1">{req.reason}</div>}
          </div>

          {hasBoard && (
            <div className="flex gap-4">
              <label className={`flex items-center gap-2 ${short ? 'text-slate-400' : ''}`}>
                <input type="radio" checked={!other} disabled={short} onChange={() => setOther(false)} /> Issue from this board
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" checked={other} onChange={() => setOther(true)} /> Issue from a different board
              </label>
            </div>
          )}
          {short && <div className="text-xs text-red-600">Not enough of the board asked for — pick another board for this job.</div>}

          {other && (
            <div>
              <input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="input w-full"
                placeholder="Search boards in stock: SKU, type, GSM, size…"
                autoFocus
              />
              <div className="mt-2 border border-slate-200 rounded max-h-64 overflow-auto">
                <table className="min-w-full text-left text-sm">
                  <tbody className="divide-y divide-slate-100">
                    {list.map((b) => {
                      const ok = b.sheetsOnHand >= req.requestedSheets;
                      return (
                        <tr
                          key={b.id}
                          onClick={() => setPick(b)}
                          className={`cursor-pointer ${pick?.id === b.id ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}
                        >
                          <td className="px-3 py-2 w-8"><input type="radio" readOnly checked={pick?.id === b.id} /></td>
                          <td className="px-3 py-2 font-mono text-xs text-slate-500 whitespace-nowrap">{b.sku}</td>
                          <td className="px-3 py-2 text-slate-800">{spec(b)}</td>
                          <td className={`px-3 py-2 text-right whitespace-nowrap font-medium ${ok ? 'text-emerald-700' : 'text-red-600'}`}>{b.sheetsOnHand.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                    {boards.isLoading && <tr><td className="px-3 py-4 text-slate-500">Loading boards…</td></tr>}
                    {!boards.isLoading && list.length === 0 && <tr><td className="px-3 py-4 text-slate-500">No board in stock matches.</td></tr>}
                  </tbody>
                </table>
              </div>
              {pick && pick.sheetsOnHand < req.requestedSheets && (
                <div className="text-xs text-red-600 mt-1">That board has only {pick.sheetsOnHand.toLocaleString()} sheets.</div>
              )}
              {!hasBoard && !pick && (
                <div className="text-xs text-slate-500 mt-1">Pick a board to issue the sheets now, or approve without one and issue them from Boards yourself.</div>
              )}
            </div>
          )}
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">Cancel</button>
          {!hasBoard && !pick && (
            <button onClick={() => onApprove()} disabled={busy} className="px-4 py-2 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 disabled:opacity-50">
              Approve without issuing
            </button>
          )}
          <button
            onClick={() => onApprove(other ? pick?.id : undefined)}
            disabled={busy || !enough}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50"
          >
            {busy ? 'Issuing…' : other && pick ? `Approve & issue from ${pick.sku}` : 'Approve & issue'}
          </button>
        </div>
      </div>
    </div>
  );
}
